import React, { useEffect, useMemo, useState } from 'react';
import {
  Box,
  Button,
  Chip,
  CircularProgress,
  IconButton,
  InputBase,
  Paper,
  Stack,
  Tab,
  Tabs,
  Tooltip,
  Typography,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  MenuItem
} from '@mui/material';
import RefreshIcon from '@mui/icons-material/Refresh';
import AddIcon from '@mui/icons-material/Add';
import VisibilityIcon from '@mui/icons-material/Visibility';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/DeleteOutline';
import DownloadIcon from '@mui/icons-material/Download';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import PendingIcon from '@mui/icons-material/Pending';
import ErrorIcon from '@mui/icons-material/Error';
import PaymentsIcon from '@mui/icons-material/Payments';
import SearchIcon from '@mui/icons-material/Search';
import FilterAltIcon from '@mui/icons-material/FilterAlt';
import RestartAltIcon from '@mui/icons-material/RestartAlt';
import { useNavigate } from 'react-router-dom';
import { deleteFundRequest, exportFundRequestExcel, listFundRequests, releaseFundRequest, getFundRequestSummary, uploadFundAttachment, exportTransferSummary, FUND_TYPES } from '../../services/fundRequestService';
import { useAuth } from '../../context/AuthContext';

const STATUS_META = {
  draft: { label: 'DRAF', color: 'default' },
  submitted: { label: 'DIAJUKAN', color: 'info' },
  in_review: { label: 'SEDANG DITINJAU', color: 'warning' },
  approved: { label: 'DISETUJUI', color: 'info', icon: <CheckCircleIcon fontSize="small" /> },
  rejected: { label: 'DITOLAK', color: 'error', icon: <ErrorIcon fontSize="small" /> },
  released: { label: 'DICAIRKAN', color: 'success', icon: <CheckCircleIcon fontSize="small" /> },
};

const statusChip = (status) => {
  const cfg = STATUS_META[status] || { label: (status || '').toUpperCase(), color: 'default' };
  return <Chip size="small" color={cfg.color} icon={cfg.icon} label={cfg.label} />;
};

const getStatusLabel = (status) => STATUS_META[status]?.label || (status || '').toUpperCase();

const getTypeLabel = (type) => {
  const normalized = String(type || '').toUpperCase();
  const map = {
    GENERAL: 'DANA UMUM',
    CASH_ADVANCE: 'KASBON / UPAH KERJA',
    TRAVEL: 'TIKET / MOBILISASI'
  };
  return map[normalized] || normalized.replace(/_/g, ' ');
};

const TYPE_FILTER_OPTIONS = [
  FUND_TYPES.GENERAL,
  FUND_TYPES.CASH_ADVANCE,
  FUND_TYPES.TRAVEL
];

const isAccountingRole = (roleName) => (roleName || '').toLowerCase() === 'accounting';
const isGsRole = (roleName) => (roleName || '').toLowerCase() === 'gs';

const getEffectiveStatus = (item) => {
  const status = (item.status || '').toLowerCase();
  if (status !== 'in_review') return status;
  const pendingNonAccounting = (item.approvalSteps || []).some(
    s => s.status === 'pending' && !isAccountingRole(s.role?.name || s.roleName)
  );
  return pendingNonAccounting ? status : 'approved';
};

const FundRequestList = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const normalizedRole = (user?.role || '').toLowerCase();
  // Roles that create fund requests (see "PENGAJUAN SAYA" tab by default).
  const FUND_REQUESTER_ROLES = [
    'staff', 'site_manager', 'site_engineer', 'commercial_staff',
    'procurement_ho_staff', 'operational_staff', 'finance_staff', 'gs',
  ];
  const isFundRequester = FUND_REQUESTER_ROLES.includes(normalizedRole);
  const isAccountingUser = isAccountingRole(user?.role);
  const isGsUser = isGsRole(user?.role);
  const [tab, setTab] = useState(isFundRequester ? 'mine' : (isAccountingUser || isGsUser) ? 'release' : 'approval');
  const [transferDate, setTransferDate] = useState('');
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState([]);
  const [deletingId, setDeletingId] = useState(null);
  const [releaseDialog, setReleaseDialog] = useState({ open: false, item: null });
  const [releaseForm, setReleaseForm] = useState({ referenceNo: '', amount: '', description: '' });
  const [releasing, setReleasing] = useState(false);
  const [releaseFile, setReleaseFile] = useState(null);
  const [summary, setSummary] = useState(null);
  const [summaryLoading, setSummaryLoading] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [typeFilter, setTypeFilter] = useState('all');
  const [projectFilter, setProjectFilter] = useState('all');
  const [requesterFilter, setRequesterFilter] = useState('all');

  useEffect(() => {
    if (!isFundRequester && tab === 'mine') {
      setTab((isAccountingUser || isGsUser) ? 'release' : 'approval');
    }
  }, [isFundRequester, isAccountingUser, isGsUser, tab]);

  const loadData = async () => {
    try {
      setLoading(true);
      let statusParam = undefined;
      if (isAccountingUser || isGsUser) {
        if (tab === 'release') statusParam = undefined; // fetch all, filter locally to effective approved
        else if (tab === 'released') statusParam = 'released';
      } else if (tab === 'approval') {
        statusParam = 'in_review';
      }
      const res = await listFundRequests({
        status: statusParam,
        plannedTransferDate: (isAccountingUser || isGsUser) && tab === 'release' && transferDate ? transferDate : undefined,
        page: 1,
        limit: 50
      });
      setData(res.rows || res.fundRequests || res.progressSubmissions || res.progress || res.requests || res.data || []);
    } catch (err) {
      console.error('Failed to load fund requests', err);
    } finally {
      setLoading(false);
    }
  };

  const loadSummary = async () => {
    if (!isAccountingUser) return; // GS doesn't need summary
    try {
      setSummaryLoading(true);
      const res = await getFundRequestSummary();
      setSummary(res?.data || res);
    } catch (err) {
      console.error('Failed to load fund request summary', err);
      setSummary(null);
    } finally {
      setSummaryLoading(false);
    }
  };

  const handleDownloadReleasedExcel = async () => {
    try {
      const today = new Date().toISOString().slice(0, 10);
      const blob = await exportTransferSummary(today);
      const url = window.URL.createObjectURL(new Blob([blob]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `released-${today}.xlsx`);
      document.body.appendChild(link);
      link.click();
      link.parentNode.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (err) {
      console.error('Failed to download released excel', err);
      alert(err.response?.data?.message || err.message || 'Gagal mengunduh Excel released');
    }
  };

  useEffect(() => {
    loadData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tab, transferDate]);
  useEffect(() => {
    loadSummary();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tab, transferDate]);

  const handleDelete = async (id) => {
    const confirm = window.confirm('HAPUS DRAF PENGAJUAN INI? TINDAKAN INI TIDAK DAPAT DIBATALKAN.');
    if (!confirm) return;
    try {
      setDeletingId(id);
      await deleteFundRequest(id);
      await loadData();
    } catch (err) {
      console.error('Failed to delete fund request', err);
      alert(err.response?.data?.message || err.message);
    } finally {
      setDeletingId(null);
    }
  };

  const handleOpenRelease = (item) => {
    setReleaseDialog({ open: true, item });
    const baseAmount = Number(item.totalAmount || 0);
    const formattedAmount = baseAmount > 0
      ? baseAmount.toLocaleString('id-ID', { maximumFractionDigits: 0 })
      : '';
    setReleaseForm({
      referenceNo: item.requestNo || '',
      amount: formattedAmount,
      description: ''
    });
    setReleaseFile(null);
  };

  const handleReleaseSubmit = async () => {
    if (!releaseDialog.item) return;
    try {
      setReleasing(true);
      const rawAmount = (releaseForm.amount || '').replace(/[^\d]/g, '');
      const fallbackAmount = Number(releaseDialog.item.totalAmount || 0);
      const amountNumber = rawAmount ? Number(rawAmount) : fallbackAmount;
      if (!releaseFile) {
        alert('Upload bukti transfer wajib diisi.');
        setReleasing(false);
        return;
      }
      const uploadRes = await uploadFundAttachment(releaseFile);
      const attachmentUrl = uploadRes?.url || uploadRes?.file?.url || uploadRes?.fileUrl;
      await releaseFundRequest(releaseDialog.item.id, {
        referenceNo: releaseForm.referenceNo,
        amount: amountNumber,
        attachmentUrl,
        description: releaseForm.description
      });
      setReleaseDialog({ open: false, item: null });
      setReleaseForm({ referenceNo: '', amount: '', description: '' });
      setReleaseFile(null);
      await loadData();
    } catch (err) {
      console.error('Failed to release fund request', err);
      alert(err.response?.data?.message || err.message || 'Gagal mencairkan');
    } finally {
      setReleasing(false);
    }
  };

  const handleDownload = async (id, requestNo) => {
    try {
      const blob = await exportFundRequestExcel(id);
      const url = window.URL.createObjectURL(new Blob([blob]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `fund-request-${requestNo || id}.xlsx`);
      document.body.appendChild(link);
      link.click();
      link.parentNode.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (err) {
      console.error('Failed to download excel', err);
      alert(err.response?.data?.message || err.message || 'Gagal download');
    }
  };

  const rows = useMemo(() => {
    if (!user) return data;
    const userRole = (user.role || '').toLowerCase();
    if (isAccountingUser) {
      if (tab === 'release') {
        return data.filter(item => getEffectiveStatus(item) === 'approved');
      }
      if (tab === 'released') {
        return data.filter(item => getEffectiveStatus(item) === 'released');
      }
    }
    if (isGsUser) {
      if (tab === 'release') {
        // GS only sees TRAVEL type for release
        return data.filter(item => getEffectiveStatus(item) === 'approved' && item.type === 'TRAVEL');
      }
      if (tab === 'released') {
        return data.filter(item => getEffectiveStatus(item) === 'released' && item.type === 'TRAVEL');
      }
    }
    if (tab !== 'approval') return data;
    return data.filter(item => {
      const steps = item.approvalSteps || [];
      const firstPending = steps.find(s => s.status === 'pending' && !isAccountingRole(s.role?.name || s.roleName));
      if (!firstPending) return false;
      const stepRole = (firstPending.role?.name || '').toLowerCase() || (firstPending.roleName || '').toLowerCase();
      return stepRole === userRole;
    });
  }, [data, tab, user, isAccountingUser, isGsUser]);

  const formatDate = (value) => {
    if (!value) return '-';
    try {
      const d = new Date(value);
      if (Number.isNaN(d.getTime())) return '-';
      return new Intl.DateTimeFormat('id-ID', { day: '2-digit', month: 'short', year: 'numeric' }).format(d);
    } catch (e) {
      return '-';
    }
  };

  const formatCurrency = (value) => {
    const numeric = Number(value || 0);
    if (!numeric) return 'Rp 0';
    return `Rp ${numeric.toLocaleString('id-ID', { maximumFractionDigits: 0 })}`;
  };

  const getProjectLabel = (item) => item.project?.name || 'HO / NON PROYEK';
  const getRequesterLabel = (item) =>
    item.requester?.fullName ||
    item.requester?.username ||
    item.requesterName ||
    item.requestedByName ||
    '-';

  const normalizeDateKey = (value) => {
    if (!value) return '';
    try {
      const date = new Date(value);
      if (Number.isNaN(date.getTime())) return '';
      return date.toISOString().slice(0, 10);
    } catch (e) {
      return '';
    }
  };

  const isReleaseContext = (isAccountingUser || isGsUser) && tab === 'release';
  const approvedTotal = summary?.totals?.approved || 0;
  const releasedTotal = summary?.totals?.released || 0;
  const remainingTotal = Math.max(approvedTotal - releasedTotal, 0);

  const typeOptions = TYPE_FILTER_OPTIONS;

  const projectOptions = useMemo(() => {
    const values = Array.from(new Set(rows.map(item => getProjectLabel(item)).filter(Boolean)));
    return values.sort((a, b) => a.localeCompare(b));
  }, [rows]);

  const requesterOptions = useMemo(() => {
    const values = Array.from(
      new Set(rows.map(item => getRequesterLabel(item)).filter(value => value && value !== '-'))
    );
    return values.sort((a, b) => a.localeCompare(b));
  }, [rows]);

  const filteredRows = useMemo(() => {
    const query = searchQuery.trim().toLowerCase();
    return rows.filter(item => {
      const transferKey = normalizeDateKey(item.plannedTransferDate);
      const projectLabel = getProjectLabel(item);
      const requesterLabel = getRequesterLabel(item);
      const effectiveStatus = getEffectiveStatus(item);

      if (typeFilter !== 'all' && String(item.type || '').trim() !== typeFilter) return false;
      if (projectFilter !== 'all' && projectLabel !== projectFilter) return false;
      if (requesterFilter !== 'all' && requesterLabel !== requesterFilter) return false;
      if (transferDate && (!transferKey || transferKey !== transferDate)) return false;
      if (!query) return true;

      const haystack = [
        item.requestNo,
        item.title,
        item.category,
        getTypeLabel(item.type),
        projectLabel,
        requesterLabel,
        getStatusLabel(effectiveStatus)
      ]
        .filter(Boolean)
        .join(' ')
        .toLowerCase();
      return haystack.includes(query);
    });
  }, [
    rows,
    searchQuery,
    typeFilter,
    projectFilter,
    requesterFilter,
    transferDate
  ]);

  const activeFilterCount = [
    searchQuery.trim(),
    typeFilter !== 'all',
    projectFilter !== 'all',
    requesterFilter !== 'all',
    transferDate
  ].filter(Boolean).length;

  const hasActiveFilters = activeFilterCount > 0;

  const resetFilters = () => {
    setSearchQuery('');
    setTypeFilter('all');
    setProjectFilter('all');
    setRequesterFilter('all');
    setTransferDate('');
  };

  return (
    <Box sx={{ width: '100%' }}>
      {/* Header with blue gradient */}
      <Paper
        elevation={0}
        sx={{
          p: { xs: 3, sm: 4 },
          mb: 4,
          borderRadius: 3,
          background: 'linear-gradient(135deg, #0f172a, #1e3a5f, #1e40af)',
          position: 'relative',
          overflow: 'hidden',
          '&::before': {
            content: '""',
            position: 'absolute',
            top: 0,
            right: 0,
            width: '40%',
            height: '100%',
            background: 'radial-gradient(circle at top right, rgba(59, 130, 246, 0.2), transparent 70%)',
            pointerEvents: 'none'
          }
        }}
      >
        <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} alignItems={{ xs: 'flex-start', md: 'center' }} justifyContent="space-between" position="relative" zIndex={1}>
          <Box>
            <Typography variant="h4" sx={{ fontWeight: 800, color: '#ffffff', mb: 1 }}>
              {isGsUser ? 'PENCAIRAN DANA PERJALANAN DINAS' : (isAccountingUser ? 'STATUS PENCAIRAN DANA' : 'Expense Request')}
            </Typography>
            <Typography variant="body1" sx={{ color: 'rgba(255, 255, 255, 0.85)' }}>
              {isGsUser ? 'Kelola pencairan dana untuk perjalanan dinas (TIKET/Mobilisasi).' : (isAccountingUser ? 'Kelola pencairan dana yang sudah disetujui.' : 'Kelola pengajuan, persetujuan, dan status pencairan dana proyek.')}
            </Typography>
          </Box>
          <Stack direction="row" spacing={1} flexWrap="wrap">
            {isFundRequester && (
              <Button
                variant="contained"
                startIcon={<AddIcon />}
                onClick={() => navigate('/fund-requests/new')}
                sx={{
                  bgcolor: 'rgba(255, 255, 255, 0.15)',
                  backdropFilter: 'blur(10px)',
                  border: '1px solid rgba(255, 255, 255, 0.2)',
                  color: '#ffffff',
                  fontWeight: 700,
                  '&:hover': {
                    bgcolor: 'rgba(255, 255, 255, 0.25)',
                    border: '1px solid rgba(255, 255, 255, 0.3)'
                  }
                }}
              >
                BUAT PENGAJUAN
              </Button>
            )}
            <Tooltip title="Muat ulang data">
              <IconButton onClick={loadData} sx={{ border: '1px solid', borderColor: 'rgba(255, 255, 255, 0.3)', color: '#ffffff', '&:hover': { bgcolor: 'rgba(255, 255, 255, 0.1)' } }}>
                <RefreshIcon />
              </IconButton>
            </Tooltip>
          </Stack>
        </Stack>
      </Paper>

      {isAccountingUser && !isGsUser && (
        <Paper
          elevation={0}
          sx={{
            p: { xs: 2.5, sm: 3 },
            mb: 3,
            borderRadius: 2,
            border: '1px solid',
            borderColor: 'rgba(15, 23, 42, 0.1)',
            bgcolor: '#F8FAFC',
            boxShadow: '0 1px 2px rgba(15, 23, 42, 0.04), 0 1px 3px rgba(15, 23, 42, 0.06)'
          }}
        >
          <Stack direction={{ xs: 'column', lg: 'row' }} spacing={2} alignItems={{ lg: 'center' }} justifyContent="space-between">
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} flexWrap="wrap">
              <Box>
                <Typography variant="caption" sx={{ color: '#64748B', fontWeight: 600 }}>TOTAL DISETUJUI</Typography>
                <Typography variant="h6" sx={{ fontWeight: 700, color: '#0f172a' }}>
                  {summaryLoading ? '...' : formatCurrency(approvedTotal)}
                </Typography>
              </Box>
              <Box>
                <Typography variant="caption" sx={{ color: '#64748B', fontWeight: 600 }}>TOTAL DICAIRKAN</Typography>
                <Typography variant="h6" sx={{ fontWeight: 700, color: '#0f172a' }}>
                  {summaryLoading ? '...' : formatCurrency(releasedTotal)}
                </Typography>
              </Box>
              <Box>
                <Typography variant="caption" sx={{ color: '#64748B', fontWeight: 600 }}>SISA BELUM DICAIRKAN</Typography>
                <Typography variant="h6" sx={{ fontWeight: 700, color: remainingTotal > 0 ? '#b45309' : '#0f172a' }}>
                  {summaryLoading ? '...' : formatCurrency(remainingTotal)}
                </Typography>
              </Box>
            </Stack>
            <Button
              variant="outlined"
              size="small"
              startIcon={<DownloadIcon />}
              onClick={handleDownloadReleasedExcel}
              disabled={summaryLoading}
              sx={{
                borderColor: 'rgba(15, 23, 42, 0.2)',
                color: '#0f172a',
                fontWeight: 600,
                '&:hover': { borderColor: 'rgba(59, 130, 246, 0.4)', backgroundColor: 'rgba(59, 130, 246, 0.04)' }
              }}
            >
              Download Rekap Excel
            </Button>
          </Stack>
        </Paper>
      )}

      {/* Tabs */}
      <Paper
        elevation={0}
        sx={{
          mb: 3,
          borderRadius: 2,
          border: '1px solid',
          borderColor: 'rgba(15, 23, 42, 0.1)',
          bgcolor: '#FFFFFF',
          boxShadow: '0 1px 2px rgba(15, 23, 42, 0.04), 0 1px 3px rgba(15, 23, 42, 0.06)',
          overflow: 'hidden'
        }}
      >
        <Tabs
          value={tab}
          onChange={(_, v) => setTab(v)}
          variant="scrollable"
          scrollButtons="auto"
          sx={{
            px: { xs: 1, sm: 2 },
            pt: 1,
            minHeight: 48,
            bgcolor: '#F8FAFC',
            borderBottom: '1px solid',
            borderColor: 'rgba(15, 23, 42, 0.08)',
            '& .MuiTabs-flexContainer': { gap: 0.5 },
            '& .MuiTab-root': {
              fontSize: '0.85rem',
              color: '#64748B',
              fontWeight: 600,
              textTransform: 'none',
              minHeight: 44,
              px: 2,
              borderRadius: '8px 8px 0 0',
              transition: 'color 160ms ease, background-color 160ms ease',
              '&:hover': {
                color: '#1e40af',
                backgroundColor: 'rgba(59, 130, 246, 0.06)'
              }
            },
            '& .Mui-selected': {
              color: '#1e40af !important',
              fontWeight: 700,
              backgroundColor: '#FFFFFF'
            },
            '& .MuiTabs-indicator': {
              height: 3,
              borderRadius: '3px 3px 0 0',
              background: 'linear-gradient(90deg, #3B82F6, #1e40af)'
            }
          }}
        >
          {isFundRequester && <Tab value="mine" label="PENGAJUAN SAYA" />}
          <Tab
            value={(isAccountingUser || isGsUser) ? 'release' : 'approval'}
            label={isGsUser ? 'MENUNGGU RELEASE (TRAVEL)' : (isAccountingUser ? 'MENUNGGU RELEASE' : 'BUTUH PERSETUJUAN')}
            icon={<PendingIcon fontSize="small" />}
            iconPosition="end"
          />
          {(isAccountingUser || isGsUser) && (
            <Tab value="released" label={isGsUser ? 'SUDAH DICAIRKAN (TRAVEL)' : 'SUDAH DICAIRKAN'} />
          )}
          <Tab value="all" label="SEMUA" />
        </Tabs>
      </Paper>

      {/* Filter Bar */}
      <Paper
        elevation={0}
        sx={{
          p: { xs: 2, sm: 2.5 },
          mb: 3,
          borderRadius: 2,
          border: '1px solid',
          borderColor: 'rgba(15, 23, 42, 0.1)',
          bgcolor: '#FFFFFF',
          boxShadow: '0 1px 2px rgba(15, 23, 42, 0.04), 0 1px 3px rgba(15, 23, 42, 0.06)'
        }}
      >
        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} alignItems={{ xs: 'flex-start', sm: 'center' }} justifyContent="space-between">
          <Stack direction="row" spacing={1} alignItems="center">
            <FilterAltIcon fontSize="small" sx={{ color: '#64748B' }} />
            <Typography variant="subtitle2" sx={{ fontWeight: 700, color: '#0f172a' }}>
              Filter Data
            </Typography>
            {hasActiveFilters && (
              <Chip size="small" color="primary" label={`${activeFilterCount} filter aktif`} sx={{ fontWeight: 600 }} />
            )}
          </Stack>
          <Button
            size="small"
            variant="text"
            startIcon={<RestartAltIcon />}
            onClick={resetFilters}
            disabled={!hasActiveFilters}
            sx={{ fontWeight: 600, color: '#3B82F6', '&:hover': { bgcolor: 'rgba(59, 130, 246, 0.04)' } }}
          >
            Reset Semua
          </Button>
        </Stack>

        <Stack direction="row" spacing={1.5} flexWrap="wrap" sx={{ mt: 2 }}>
          <Paper
            elevation={0}
            sx={{
              px: 1.75,
              py: 0.75,
              display: 'flex',
              alignItems: 'center',
              width: { xs: '100%', sm: 300 },
              borderRadius: 2,
              border: '1px solid',
              borderColor: 'rgba(15, 23, 42, 0.1)',
              bgcolor: '#F8FAFC'
            }}
          >
            <SearchIcon fontSize="small" sx={{ color: '#64748B' }} />
            <InputBase
              sx={{ ml: 1, flex: 1, fontSize: '0.9rem', color: '#0f172a' }}
              placeholder="Cari nomor, judul, proyek, pengaju, tipe..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
            />
          </Paper>

          <TextField
            select
            size="small"
            label="Tipe"
            value={typeFilter}
            onChange={(e) => setTypeFilter(e.target.value)}
            sx={{ width: { xs: '100%', sm: 230 } }}
          >
            <MenuItem value="all">Semua Tipe</MenuItem>
            {typeOptions.map((type) => (
              <MenuItem key={type} value={type}>
                {getTypeLabel(type)}
              </MenuItem>
            ))}
          </TextField>

          <TextField
            select
            size="small"
            label="Proyek"
            value={projectFilter}
            onChange={(e) => setProjectFilter(e.target.value)}
            sx={{ width: { xs: '100%', sm: 280 } }}
          >
            <MenuItem value="all">Semua Proyek</MenuItem>
            {projectOptions.map((project) => (
              <MenuItem key={project} value={project}>
                {project}
              </MenuItem>
            ))}
          </TextField>

          <TextField
            select
            size="small"
            label="Pengaju"
            value={requesterFilter}
            onChange={(e) => setRequesterFilter(e.target.value)}
            sx={{ width: { xs: '100%', sm: 230 } }}
          >
            <MenuItem value="all">Semua Pengaju</MenuItem>
            {requesterOptions.map((requester) => (
              <MenuItem key={requester} value={requester}>
                {requester}
              </MenuItem>
            ))}
          </TextField>

          {isAccountingUser && (
            <TextField
              type="date"
              size="small"
              label="Tanggal Transfer"
              InputLabelProps={{ shrink: true }}
              value={transferDate}
              onChange={(e) => setTransferDate(e.target.value)}
              sx={{ width: { xs: '100%', sm: 180 } }}
            />
          )}
        </Stack>

        {hasActiveFilters && (
          <Stack direction="row" spacing={1} flexWrap="wrap" sx={{ mt: 2 }}>
            {searchQuery && (
              <Chip
                size="small"
                variant="outlined"
                label={`Cari: ${searchQuery}`}
                onDelete={() => setSearchQuery('')}
                sx={{ borderColor: 'rgba(15, 23, 42, 0.2)', color: '#475569', fontWeight: 600 }}
              />
            )}
            {typeFilter !== 'all' && (
              <Chip
                size="small"
                variant="outlined"
                label={`Tipe: ${getTypeLabel(typeFilter)}`}
                onDelete={() => setTypeFilter('all')}
                sx={{ borderColor: 'rgba(15, 23, 42, 0.2)', color: '#475569', fontWeight: 600 }}
              />
            )}
            {projectFilter !== 'all' && (
              <Chip
                size="small"
                variant="outlined"
                label={`Proyek: ${projectFilter}`}
                onDelete={() => setProjectFilter('all')}
                sx={{ borderColor: 'rgba(15, 23, 42, 0.2)', color: '#475569', fontWeight: 600 }}
              />
            )}
            {requesterFilter !== 'all' && (
              <Chip
                size="small"
                variant="outlined"
                label={`Pengaju: ${requesterFilter}`}
                onDelete={() => setRequesterFilter('all')}
                sx={{ borderColor: 'rgba(15, 23, 42, 0.2)', color: '#475569', fontWeight: 600 }}
              />
            )}
            {transferDate && (
              <Chip
                size="small"
                variant="outlined"
                label={`Transfer: ${transferDate}`}
                onDelete={() => setTransferDate('')}
                sx={{ borderColor: 'rgba(15, 23, 42, 0.2)', color: '#475569', fontWeight: 600 }}
              />
            )}
          </Stack>
        )}
      </Paper>

      {loading ? (
        <Box display="flex" justifyContent="center" py={4}><CircularProgress sx={{ color: '#3B82F6' }} /></Box>
      ) : (
        <Paper
          elevation={0}
          sx={{
            p: { xs: 2, sm: 2.5 },
            borderRadius: 2,
            border: '1px solid',
            borderColor: 'rgba(15, 23, 42, 0.1)',
            bgcolor: '#FFFFFF',
            boxShadow: '0 1px 2px rgba(15, 23, 42, 0.04), 0 1px 3px rgba(15, 23, 42, 0.06)'
          }}
        >
          {filteredRows.length === 0 && (
            <Box p={4} textAlign="center">
              <Typography variant="h6" sx={{ color: '#0f172a', fontWeight: 700, mb: 2 }}>BELUM ADA DATA</Typography>
              <Typography variant="body2" sx={{ color: '#64748B' }}>Silakan sesuaikan filter atau buat pengajuan baru.</Typography>
            </Box>
          )}
          <Stack spacing={2}>
            {filteredRows.map(item => {
              const isMaker = user && (item.requester?.id === user.id || item.requestedBy === user.id);
              const hasAnyApproval = (item.approvalSteps || []).some(step => step.status === 'approved');
              const isAwaitingFirstApproval = item.status === 'in_review' && !hasAnyApproval;
              const isSubmittedPending = item.status === 'submitted' && !hasAnyApproval;
              const canEditDraft = !isAccountingUser && isMaker && (item.status === 'draft' || isAwaitingFirstApproval || isSubmittedPending);
              const effectiveStatus = getEffectiveStatus(item);
              const nextPending = (item.approvalSteps || []).find(
                s => s.status === 'pending' && !isAccountingRole(s.role?.name || s.roleName)
              );
              return (
                <Box
                  key={item.id}
                  sx={{
                    border: '1px solid',
                    borderColor: 'rgba(15, 23, 42, 0.12)',
                    borderRadius: 2,
                    p: { xs: 2, sm: 2.5 },
                    bgcolor: '#FFFFFF',
                    boxShadow: '0 1px 2px rgba(15, 23, 42, 0.04), 0 1px 3px rgba(15, 23, 42, 0.06)',
                    transition: 'transform 160ms ease, box-shadow 160ms ease, border-color 160ms ease',
                    '&:hover': {
                      transform: 'translateY(-1px)',
                      boxShadow: '0 4px 12px rgba(15, 23, 42, 0.08)',
                      borderColor: 'rgba(59, 130, 246, 0.2)'
                    }
                  }}
                >
                  <Stack direction={{ xs: 'column', lg: 'row' }} spacing={2} alignItems={{ lg: 'center' }} justifyContent="space-between">
                    <Box sx={{ minWidth: 240, flex: 1 }}>
                      <Typography variant="subtitle1" sx={{ fontWeight: 700, color: '#0f172a' }}>
                        {item.requestNo}
                      </Typography>
                      <Typography variant="body2" sx={{ color: '#475569' }}>
                        {item.title || item.category || '-'}
                      </Typography>
                      <Typography variant="caption" sx={{ color: '#64748B', fontWeight: 600, display: 'block', mt: 0.5 }}>
                        PEMBUAT: {getRequesterLabel(item)}
                      </Typography>
                      <Stack direction="row" spacing={1} flexWrap="wrap" sx={{ mt: 1 }}>
                        <Chip size="small" variant="outlined" label={`PROYEK: ${getProjectLabel(item)}`} sx={{ borderColor: 'rgba(15, 23, 42, 0.2)', color: '#475569', fontWeight: 600 }} />
                        <Chip size="small" variant="outlined" label={`TIPE: ${getTypeLabel(item.type) || '-'}`} sx={{ borderColor: 'rgba(15, 23, 42, 0.2)', color: '#475569', fontWeight: 600 }} />
                      </Stack>
                    </Box>

                    <Stack direction="row" spacing={2} flexWrap="wrap" sx={{ minWidth: 220 }}>
                      <Box>
                        <Typography variant="caption" sx={{ color: '#64748B', fontWeight: 600 }}>NOMINAL</Typography>
                        <Typography variant="body2" sx={{ fontWeight: 700, color: '#0f172a' }}>
                          {formatCurrency(item.totalAmount || item.amount)}
                        </Typography>
                      </Box>
                      <Box>
                        <Typography variant="caption" sx={{ color: '#64748B', fontWeight: 600 }}>TGL PENGAJUAN</Typography>
                        <Typography variant="body2" sx={{ fontWeight: 600, color: '#475569' }}>
                          {formatDate(item.submissionDate)}
                        </Typography>
                      </Box>
                      <Box>
                        <Typography variant="caption" sx={{ color: '#64748B', fontWeight: 600 }}>TGL PENCAIRAN</Typography>
                        <Typography variant="body2" sx={{ fontWeight: 600, color: '#475569' }}>
                          {formatDate(item.plannedTransferDate || item.submissionDate)}
                        </Typography>
                      </Box>
                    </Stack>

                    <Stack spacing={0.75} alignItems={{ xs: 'flex-start', lg: 'flex-end' }} sx={{ minWidth: 190 }}>
                      {statusChip(effectiveStatus)}
                      {effectiveStatus === 'in_review' && (
                        <Chip
                          size="small"
                          variant="outlined"
                          label={`BERIKUTNYA: ${(nextPending?.role?.name || nextPending?.roleName || '-').toUpperCase()}`}
                          sx={{ maxWidth: 220, justifyContent: 'center', whiteSpace: 'normal', textAlign: 'center', borderColor: 'rgba(15, 23, 42, 0.2)', color: '#475569', fontWeight: 600 }}
                        />
                      )}
                    </Stack>

                    <Stack direction="row" spacing={1} justifyContent={{ xs: 'flex-start', lg: 'flex-end' }} alignItems="center">
                      {canEditDraft && (
                        <Tooltip title="EDIT DRAF">
                          <IconButton onClick={() => navigate(`/fund-requests/${item.id}/edit`)} sx={{ color: '#64748B', '&:hover': { color: '#3B82F6', bgcolor: 'rgba(59, 130, 246, 0.04)' } }}>
                            <EditIcon />
                          </IconButton>
                        </Tooltip>
                      )}
                      {isReleaseContext && effectiveStatus === 'approved' && (
                        <Tooltip title="UNDUH EXCEL">
                          <IconButton onClick={() => handleDownload(item.id, item.requestNo)} sx={{ color: '#64748B', '&:hover': { color: '#3B82F6', bgcolor: 'rgba(59, 130, 246, 0.04)' } }}>
                            <DownloadIcon />
                          </IconButton>
                        </Tooltip>
                      )}
                      {isReleaseContext && effectiveStatus === 'approved' && (!isGsUser || item.type === 'TRAVEL') && (
                        <Tooltip title="CAIRKAN">
                          <span>
                            <IconButton color="success" onClick={() => handleOpenRelease(item)} disabled={releasing}>
                              <PaymentsIcon />
                            </IconButton>
                          </span>
                        </Tooltip>
                      )}
                      <Tooltip title="LIHAT DETAIL">
                        <IconButton onClick={() => navigate(`/fund-requests/${item.id}`)} sx={{ color: '#64748B', '&:hover': { color: '#3B82F6', bgcolor: 'rgba(59, 130, 246, 0.04)' } }}><VisibilityIcon /></IconButton>
                      </Tooltip>
                      {canEditDraft && (
                        <Tooltip title="HAPUS DRAF">
                          <span>
                            <IconButton
                              color="error"
                              disabled={deletingId === item.id}
                              onClick={() => handleDelete(item.id)}
                            >
                              <DeleteIcon />
                            </IconButton>
                          </span>
                        </Tooltip>
                      )}
                    </Stack>
                  </Stack>
                </Box>
              );
            })}
          </Stack>
        </Paper>
      )}
      <Typography variant="caption" sx={{ color: '#64748B', mt: 1, display: 'block', fontWeight: 600 }}>
        Menampilkan {filteredRows.length} dari {rows.length} data
      </Typography>

      <Dialog open={releaseDialog.open} onClose={() => !releasing && setReleaseDialog({ open: false, item: null })} maxWidth="sm" fullWidth>
        <DialogTitle>Konfirmasi Pencairan</DialogTitle>
        <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
          <TextField
            label="Nomor Referensi"
            value={releaseForm.referenceNo}
            onChange={(e) => setReleaseForm(prev => ({ ...prev, referenceNo: e.target.value }))}
            fullWidth
          />
          <TextField
            label="Jumlah Cair"
            type="text"
            inputMode="numeric"
            value={releaseForm.amount}
            onChange={(e) => {
              const raw = e.target.value.replace(/\./g, '').replace(/,/g, '');
              const numeric = raw.replace(/[^\d]/g, '');
              const formatted = numeric ? Number(numeric).toLocaleString('id-ID', { maximumFractionDigits: 0 }) : '';
              setReleaseForm(prev => ({ ...prev, amount: formatted }));
            }}
            fullWidth
          />
          <TextField
            label="Keterangan"
            multiline
            minRows={2}
            value={releaseForm.description}
            onChange={(e) => setReleaseForm(prev => ({ ...prev, description: e.target.value }))}
            fullWidth
            placeholder="Catatan pencairan (opsional)"
          />
          <Button
            variant="outlined"
            component="label"
            disabled={releasing}
          >
            {releaseFile ? `Bukti: ${releaseFile.name}` : 'Upload Bukti Transfer (wajib)'}
            <input
              type="file"
              hidden
              accept=".jpg,.jpeg,.png,.pdf"
              onChange={(e) => {
                const file = e.target.files?.[0];
                if (file) setReleaseFile(file);
              }}
            />
          </Button>
          <Typography variant="body2" color="text.secondary">
            Pastikan data sesuai slip transfer sebelum menekan "Cairkan".
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setReleaseDialog({ open: false, item: null })} disabled={releasing}>Batal</Button>
          <Button variant="contained" onClick={handleReleaseSubmit} disabled={releasing}>
            {releasing ? 'Memproses...' : 'Cairkan'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default FundRequestList;
